import { z } from 'zod';
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns';
import {
  JobSortField,
  JobSortOption,
  JobSortOrder,
  JobTableType,
  StatusValue,
} from 'api/jobApi';
import { ACTIVITY_BUCKETS, ActivityBucket } from 'constants/activityBuckets';

export const JOB_LIST_PATH = '/jobs';

export interface JobListUrlState {
  tab: JobTableType;
  status: StatusValue[];
  state: string[];
  client: string[];
  branch: string[];
  coordinator: string[];
  supervisor: string[];
  activity: ActivityBucket[];
  activityFrom: string | null;
  activityTo: string | null;
  sort: JobSortOption | null;
  search: string;
  page: number;
  pageSize: number;
}

export type PartialJobListUrlState = Partial<JobListUrlState>;

const TAB_SLUG_TO_TYPE: Record<string, JobTableType> = {
  open: JobTableType.OPEN,
  all: JobTableType.ALL,
  closed: JobTableType.CLOSED,
  mine: JobTableType.MY_JOBS,
};

const TAB_TYPE_TO_SLUG: Record<JobTableType, string> = {
  [JobTableType.OPEN]: 'open',
  [JobTableType.ALL]: 'all',
  [JobTableType.CLOSED]: 'closed',
  [JobTableType.MY_JOBS]: 'mine',
};

const statusSlugFromValue = (value: StatusValue): string =>
  value.toLowerCase().replace(/\s+/g, '-');

const STATUS_SLUG_TO_VALUE: Record<string, StatusValue> = (
  Object.values(StatusValue) as StatusValue[]
).reduce((acc, value) => {
  acc[statusSlugFromValue(value)] = value;
  return acc;
}, {} as Record<string, StatusValue>);

const ACTIVITY_SLUG_TO_KEY: Record<string, ActivityBucket> =
  ACTIVITY_BUCKETS.reduce((acc, bucket) => {
    acc[bucket.key.toLowerCase()] = bucket.key;
    return acc;
  }, {} as Record<string, ActivityBucket>);

const SORT_FIELDS: JobSortField[] = [
  'jobNo',
  'createdOn',
  'lastAttendance',
  'claimNumber',
  'workOrderNo',
];
const SORT_FIELD_SET = new Set<string>(SORT_FIELDS);

const PAGE_SIZE_OPTIONS = [10, 15, 20, 30, 50];

const PARAM = {
  tab: 'tab',
  status: 'status',
  state: 'state',
  client: 'client',
  branch: 'branch',
  coordinator: 'coordinator',
  supervisor: 'supervisor',
  activity: 'activity',
  activityFrom: 'activityFrom',
  activityTo: 'activityTo',
  sort: 'sort',
  search: 'q',
  page: 'page',
  pageSize: 'pageSize',
} as const;

const ALL_PARAM_KEYS: readonly string[] = Object.values(PARAM);

const tabSlugSchema = z
  .string()
  .refine((slug): slug is keyof typeof TAB_SLUG_TO_TYPE =>
    Object.prototype.hasOwnProperty.call(TAB_SLUG_TO_TYPE, slug)
  );

const statusSlugSchema = z
  .string()
  .refine((slug): slug is string =>
    Object.prototype.hasOwnProperty.call(STATUS_SLUG_TO_VALUE, slug)
  );

const activitySlugSchema = z
  .string()
  .refine((slug): slug is string =>
    Object.prototype.hasOwnProperty.call(ACTIVITY_SLUG_TO_KEY, slug.toLowerCase())
  );

const sortSchema = z
  .string()
  .regex(/^([^:]+):([^:]+)$/, 'sort must be in the form field:order')
  .transform((value, ctx): JobSortOption | null => {
    const [fieldRaw, orderRaw] = value.split(':');
    const field = fieldRaw as JobSortField;
    const order = orderRaw.toUpperCase() as JobSortOrder;
    if (!SORT_FIELD_SET.has(field)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `unknown sort field: ${fieldRaw}`,
      });
      return null;
    }
    if (order !== 'ASC' && order !== 'DESC') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `unknown sort order: ${orderRaw}`,
      });
      return null;
    }
    return { field, order };
  });

const dateOnlySchema = z
  .string()
  .refine((value) => isValid(parseISO(value)), 'invalid date');

const pageSchema = z
  .string()
  .regex(/^\d+$/, 'page must be a positive integer')
  .transform(Number);

const pageSizeSchema = z
  .string()
  .regex(/^\d+$/, 'pageSize must be an integer')
  .transform(Number)
  .refine((n): n is number => PAGE_SIZE_OPTIONS.includes(n));

const nonEmptyStringSchema = z
  .string()
  .refine((value) => value.length > 0, 'must not be empty');

function parseValidArray<T>(
  sp: URLSearchParams,
  key: string,
  schema: z.ZodType<string>,
  map: (slug: string) => T,
  normalize?: (slug: string) => string
): T[] | undefined {
  const values = sp.getAll(key);
  if (values.length === 0) return undefined;
  const result: T[] = [];
  for (const raw of values) {
    const candidate = normalize ? normalize(raw) : raw;
    const parsed = schema.safeParse(candidate);
    if (parsed.success) {
      result.push(map(parsed.data));
    }
  }
  return result.length > 0 ? result : undefined;
}

function parsePlainStringArray(
  sp: URLSearchParams,
  key: string
): string[] | undefined {
  const values = sp.getAll(key);
  if (values.length === 0) return undefined;
  const result = values.filter((value) => {
    const parsed = nonEmptyStringSchema.safeParse(value);
    return parsed.success;
  });
  return result.length > 0 ? result : undefined;
}

function parseDateBound(
  sp: URLSearchParams,
  key: string,
  bound: 'start' | 'end'
): string | null | undefined {
  const values = sp.getAll(key);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  const parsed = dateOnlySchema.safeParse(last);
  if (!parsed.success) return undefined;
  const date = parseISO(last);
  return bound === 'start'
    ? startOfDay(date).toISOString()
    : endOfDay(date).toISOString();
}

function parseSort(
  sp: URLSearchParams
): JobSortOption | null | undefined {
  const values = sp.getAll(PARAM.sort);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  const parsed = sortSchema.safeParse(last);
  if (!parsed.success) return undefined;
  return parsed.data;
}

function parsePage(sp: URLSearchParams): number | undefined {
  const values = sp.getAll(PARAM.page);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  const parsed = pageSchema.safeParse(last);
  if (!parsed.success) return undefined;
  return parsed.data - 1;
}

function parsePageSize(sp: URLSearchParams): number | undefined {
  const values = sp.getAll(PARAM.pageSize);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  const parsed = pageSizeSchema.safeParse(last);
  if (!parsed.success) return undefined;
  return parsed.data;
}

function parseTab(
  sp: URLSearchParams
): JobTableType | undefined {
  const values = sp.getAll(PARAM.tab);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  const parsed = tabSlugSchema.safeParse(last);
  if (!parsed.success) return undefined;
  return TAB_SLUG_TO_TYPE[last];
}

function parseSearch(sp: URLSearchParams): string | undefined {
  const values = sp.getAll(PARAM.search);
  if (values.length === 0) return undefined;
  const last = values[values.length - 1];
  return last;
}

function formatDateOnly(iso: string): string | null {
  const date = parseISO(iso);
  if (!isValid(date)) return null;
  return format(date, 'yyyy-MM-dd');
}

function isMineTab(state: PartialJobListUrlState): boolean {
  return state.tab === JobTableType.MY_JOBS;
}

export function parseJobListParams(
  sp: URLSearchParams
): PartialJobListUrlState {
  const result: PartialJobListUrlState = {};

  const tab = parseTab(sp);
  if (tab !== undefined) result.tab = tab;

  const status = parseValidArray(
    sp,
    PARAM.status,
    statusSlugSchema,
    (slug) => STATUS_SLUG_TO_VALUE[slug]
  );
  if (status !== undefined) result.status = status;

  const stateValues = parsePlainStringArray(sp, PARAM.state);
  if (stateValues !== undefined) result.state = stateValues;

  const clientValues = parsePlainStringArray(sp, PARAM.client);
  if (clientValues !== undefined) result.client = clientValues;

  const branchValues = parsePlainStringArray(sp, PARAM.branch);
  if (branchValues !== undefined) result.branch = branchValues;

  if (!isMineTab(result)) {
    const coordinatorValues = parsePlainStringArray(sp, PARAM.coordinator);
    if (coordinatorValues !== undefined) result.coordinator = coordinatorValues;

    const supervisorValues = parsePlainStringArray(sp, PARAM.supervisor);
    if (supervisorValues !== undefined) result.supervisor = supervisorValues;
  }

  const activity = parseValidArray(
    sp,
    PARAM.activity,
    activitySlugSchema,
    (slug) => ACTIVITY_SLUG_TO_KEY[slug.toLowerCase()]
  );
  if (activity !== undefined) result.activity = activity;

  const activityFrom = parseDateBound(sp, PARAM.activityFrom, 'start');
  if (activityFrom !== undefined) result.activityFrom = activityFrom;

  const activityTo = parseDateBound(sp, PARAM.activityTo, 'end');
  if (activityTo !== undefined) result.activityTo = activityTo;

  const sort = parseSort(sp);
  if (sort !== undefined) result.sort = sort;

  const search = parseSearch(sp);
  if (search !== undefined) result.search = search;

  const page = parsePage(sp);
  if (page !== undefined) result.page = page;

  const pageSize = parsePageSize(sp);
  if (pageSize !== undefined) result.pageSize = pageSize;

  return result;
}

function appendRepeated(
  sp: URLSearchParams,
  key: string,
  values: string[] | undefined
): void {
  if (!values || values.length === 0) return;
  values.forEach((value) => sp.append(key, value));
}

function appendDate(
  sp: URLSearchParams,
  key: string,
  iso: string | null | undefined
): void {
  if (!iso) return;
  const dateOnly = formatDateOnly(iso);
  if (dateOnly) sp.append(key, dateOnly);
}

export function serializeJobListParams(
  state: PartialJobListUrlState
): URLSearchParams {
  const sp = new URLSearchParams();

  if (state.tab !== undefined) {
    sp.append(PARAM.tab, TAB_TYPE_TO_SLUG[state.tab]);
  }

  if (state.status && state.status.length > 0) {
    state.status.forEach((value) =>
      sp.append(PARAM.status, statusSlugFromValue(value))
    );
  }

  appendRepeated(sp, PARAM.state, state.state);
  appendRepeated(sp, PARAM.client, state.client);
  appendRepeated(sp, PARAM.branch, state.branch);

  if (!isMineTab(state)) {
    appendRepeated(sp, PARAM.coordinator, state.coordinator);
    appendRepeated(sp, PARAM.supervisor, state.supervisor);
  }

  if (state.activity && state.activity.length > 0) {
    state.activity.forEach((bucket) =>
      sp.append(PARAM.activity, bucket.toLowerCase())
    );
  }

  appendDate(sp, PARAM.activityFrom, state.activityFrom);
  appendDate(sp, PARAM.activityTo, state.activityTo);

  if (state.sort) {
    sp.append(
      PARAM.sort,
      `${state.sort.field}:${state.sort.order.toLowerCase()}`
    );
  }

  if (state.search && state.search.trim().length > 0) {
    sp.append(PARAM.search, state.search);
  }

  if (state.page !== undefined && state.page > 0) {
    sp.append(PARAM.page, String(state.page + 1));
  }

  if (
    state.pageSize !== undefined &&
    PAGE_SIZE_OPTIONS.includes(state.pageSize) &&
    state.pageSize !== 10
  ) {
    sp.append(PARAM.pageSize, String(state.pageSize));
  }

  return sp;
}

export function hasAnyJobListParam(sp: URLSearchParams): boolean {
  return ALL_PARAM_KEYS.some((key) => sp.has(key));
}

export function buildJobListUrl(state: PartialJobListUrlState): string {
  const sp = serializeJobListParams(state);
  const query = sp.toString();
  return query ? `${JOB_LIST_PATH}?${query}` : JOB_LIST_PATH;
}
